import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Save, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/adminApi";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { invalidatePageContent } from "@/lib/usePageContent";

const PAGES = [
    { key: "home", label: "Home" },
    { key: "about", label: "About" },
    { key: "framework", label: "Framework" },
    { key: "snapshot", label: "Snapshot" },
    { key: "foundations", label: "Foundations" },
    { key: "workshop", label: "Workshop" },
    { key: "discovery", label: "Discovery" },
    { key: "contact", label: "Contact" },
];

const PageContentManager = () => {
    const [page, setPage] = useState("home");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [drafts, setDrafts] = useState({}); // { block_id: value }
    const [savingId, setSavingId] = useState(null);

    const load = async (p) => {
        setLoading(true);
        try {
            const { data } = await adminApi.get(`/admin/page-content?page=${p}`);
            setRows(data);
            setDrafts({});
        } catch {
            toast.error("Could not load page content.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(page); }, [page]);

    const isDirty = (r) => drafts[r.id] !== undefined && drafts[r.id] !== r.value;

    const save = async (r) => {
        setSavingId(r.id);
        try {
            const body = { ...r, value: drafts[r.id] ?? r.value };
            delete body.id;
            delete body.created_at;
            delete body.updated_at;
            await adminApi.put(`/admin/page-content/${r.id}`, body);
            invalidatePageContent(r.page);
            toast.success("Saved.");
            await load(page);
        } catch {
            toast.error("Save failed.");
        } finally {
            setSavingId(null);
        }
    };

    const grouped = useMemo(() => {
        const sections = {};
        rows.forEach((r) => {
            const section = (r.block_key.split(".")[0] || "misc").toUpperCase();
            (sections[section] ||= []).push(r);
        });
        return sections;
    }, [rows]);

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                    <h2 className="font-montserrat font-bold text-indigo_brand text-2xl">Site Copy</h2>
                    <p className="font-opensans text-sm text-[#4A5568] mt-1">
                        Edit every headline, subtitle, and body paragraph across your public pages. No credits, no code, no redeploy.
                    </p>
                </div>
                <div>
                    <Label>Page</Label>
                    <select className="mt-1 block border rounded-md p-2 text-sm min-w-[200px]" value={page} onChange={(e) => setPage(e.target.value)} data-testid="pagecontent-page-select">
                        {PAGES.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                    </select>
                </div>
            </div>

            {loading ? (
                <div className="ih-card p-10 text-center text-[#4A5568]"><Loader2 className="inline animate-spin mr-2" size={16}/>Loading…</div>
            ) : rows.length === 0 ? (
                <div className="ih-card p-10 text-center text-[#4A5568]">No content blocks for this page.</div>
            ) : (
                Object.entries(grouped).map(([section, blocks]) => (
                    <div key={section} className="ih-card p-6">
                        <div className="font-poppins text-[11px] font-semibold uppercase tracking-[0.2em] text-green_brand mb-4">{section}</div>
                        <div className="space-y-5">
                            {blocks.map((r) => {
                                const value = drafts[r.id] ?? r.value;
                                const dirty = isDirty(r);
                                const Field = r.block_type === "longtext" ? Textarea : Input;
                                return (
                                    <div key={r.id} data-testid={`block-${r.block_key}`}>
                                        <div className="flex items-center justify-between gap-3 mb-2">
                                            <Label className="font-raleway font-semibold text-indigo_brand text-sm">
                                                {r.label}
                                                <span className="ml-2 font-mono text-[10px] text-[#4A5568] font-normal">({r.block_key})</span>
                                            </Label>
                                            <div className="flex items-center gap-2">
                                                {dirty && (
                                                    <button onClick={() => setDrafts((d) => { const n = { ...d }; delete n[r.id]; return n; })}
                                                        className="text-[#4A5568] hover:text-indigo_brand text-xs inline-flex items-center gap-1">
                                                        <RotateCcw size={12}/>Reset
                                                    </button>
                                                )}
                                                <button
                                                    disabled={!dirty || savingId === r.id}
                                                    onClick={() => save(r)}
                                                    data-testid={`save-${r.block_key}`}
                                                    className="ih-btn-primary disabled:opacity-40 disabled:cursor-not-allowed text-xs py-1 px-3"
                                                >
                                                    {savingId === r.id ? <Loader2 className="animate-spin" size={12}/> : <Save size={12}/>}
                                                    {savingId === r.id ? "Saving…" : "Save"}
                                                </button>
                                            </div>
                                        </div>
                                        {r.block_type === "longtext" ? (
                                            <Textarea rows={3} value={value} onChange={(e) => setDrafts({ ...drafts, [r.id]: e.target.value })} />
                                        ) : (
                                            <Input value={value} onChange={(e) => setDrafts({ ...drafts, [r.id]: e.target.value })} />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))
            )}
        </div>
    );
};

export default PageContentManager;
